import React from 'react';
import {
  parseSenderDisplay,
  extractEmail,
  getSenderInitial,
  getAvatarColor,
  looksLikeHtml,
  formatFullMailDate,
} from '../utils/mailViewHelpers';
import { enhanceHtmlLinks, linkifyPlainText } from '../utils/mailLinks';
import AiAssistantPanel from './ai/AiAssistantPanel';

/**
 * Shared Inbox-style reading pane so every folder shows the opened mail with
 * the same card / thread look.
 */
const MailReadingPane = ({
  mail,
  folderLabel = 'Mail',
  onClose,
  headerActions = [],
  headerExtras = null,
  children,
}) => {
  if (!mail) return null;

  const avatarColors = getAvatarColor(mail.from);
  const senderName = parseSenderDisplay(mail.from);
  const senderEmail = extractEmail(mail.from);
  const body = String(mail.html || mail.body || mail.text || '');

  return (
    <div className="mail-detail-container">
      <div className="mail-detail-header">
        <button onClick={onClose} className="back-button" aria-label="Back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <div className="header-subject-section">
          <h3 className="header-subject">{mail.subject || '(No subject)'}</h3>
          {folderLabel ? <span className="folder-label">{folderLabel}</span> : null}
          {headerExtras}
        </div>
        {headerActions.length > 0 && (
          <div className="header-actions-right">
            {headerActions.map((action) => (
              <button
                type="button"
                key={action.key || action.title}
                className="header-action-btn"
                title={action.title}
                aria-label={action.title}
                onClick={(e) => action.onClick(e, mail)}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mail-detail-scroll">
        <div className="mail-detail-card fv-thread-card">
          <div className="fv-thread-head">
            <div
              className="mail-sender-avatar fv-thread-avatar"
              style={{ backgroundColor: avatarColors.bg, color: avatarColors.color }}
              aria-hidden="true"
            >
              {getSenderInitial(mail.from)}
            </div>
            <div className="fv-thread-meta">
              <div className="fv-thread-from">
                <strong>{senderName}</strong>
                {senderEmail && senderEmail !== senderName && (
                  <span className="fv-thread-email">&lt;{senderEmail}&gt;</span>
                )}
              </div>
              {mail.to && <div className="fv-thread-to">to {mail.to}</div>}
            </div>
            <span className="fv-thread-date">{formatFullMailDate(mail.createdAt || mail.date)}</span>
          </div>

          <div className="mail-body fv-thread-body">
            {looksLikeHtml(body) ? (
              <div
                className="body-content mail-html-body"
                dangerouslySetInnerHTML={{ __html: enhanceHtmlLinks(body) }}
              />
            ) : (
              <div
                className="body-content"
                style={{ whiteSpace: 'pre-wrap' }}
                dangerouslySetInnerHTML={{ __html: linkifyPlainText(body) }}
              />
            )}
          </div>

          {children}
        </div>
      </div>

      <AiAssistantPanel email={mail} />
    </div>
  );
};

export default MailReadingPane;
